/**
 * Stock: what is held, what moved, and the count that reconciles them.
 *
 * Two things the types are careful about.
 *
 * `quantity` can be negative, and that is not an error state. A kitchen that
 * used a delivery before booking it in has genuinely used it; the negative is
 * a prompt to count, and a UI that clamps it at zero hides the prompt.
 *
 * `reorder_level` is `string | null`, and null means nobody has said what low
 * is. That is not the same as zero. `below_reorder` is computed server-side so
 * the two cannot drift apart — never re-derive it on the client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

/** The units the server knows. Anything else is refused rather than guessed. */
export const UNITS = [
  { code: 'g', name: 'gram', dimension: 'mass' },
  { code: 'kg', name: 'kilogram', dimension: 'mass' },
  { code: 'mg', name: 'milligram', dimension: 'mass' },
  { code: 'oz', name: 'ounce', dimension: 'mass' },
  { code: 'lb', name: 'pound', dimension: 'mass' },
  { code: 'ml', name: 'millilitre', dimension: 'volume' },
  { code: 'cl', name: 'centilitre', dimension: 'volume' },
  { code: 'l', name: 'litre', dimension: 'volume' },
  { code: 'floz', name: 'fluid ounce', dimension: 'volume' },
  { code: 'pt', name: 'pint', dimension: 'volume' },
  { code: 'ea', name: 'each', dimension: 'count' },
  { code: 'dz', name: 'dozen', dimension: 'count' },
] as const;

export type UnitCode = (typeof UNITS)[number]['code'];
export type Dimension = (typeof UNITS)[number]['dimension'];

export interface StockItem {
  id: string;
  sku: string;
  name: string;
  category: string | null;
  stock_unit: string;
  /** Grams per millilitre. Null where the item cannot cross weight and volume. */
  density: string | null;
  pack_size: string | null;
  pack_unit: string | null;
  /** Null means nobody has said what low is — not zero. */
  reorder_level: string | null;
  is_active: boolean;
  /**
   * Which of the fourteen this contains.
   *
   * Null means nobody has said. An empty array means somebody looked and found
   * none of them. Those are different claims and only one is safe to print on
   * a menu, so never render the first as the second.
   */
  allergens: Allergen[] | null;
}

export interface Storage {
  id: string;
  location_id: string;
  code: string;
  name: string;
  /** Where a picker should open. The list comes back with it first. */
  is_default: boolean;
  is_active: boolean;
}

export interface StockLevel {
  stock_item_id: string;
  storage_id: string;
  sku: string;
  name: string;
  stock_unit: string;
  /** May be negative. See the note at the top of this file. */
  quantity: string;
  unit_cost: string;
  value: string;
  reorder_level: string | null;
  below_reorder: boolean;
}

export type MovementKind =
  | 'receipt'
  | 'issue'
  | 'waste'
  | 'count'
  | 'transfer_in'
  | 'transfer_out'
  /** Sent back to the supplier against a credit. Not use, not loss. */
  | 'return_out';

export interface StockMovement {
  id: string;
  stock_item_id: string;
  storage_id: string;
  kind: MovementKind;
  /** Signed: positive puts stock in, negative takes it out. */
  quantity: string;
  unit_cost: string;
  value: string;
  reason: string | null;
  source: string | null;
  occurred_at: string;
  actor_id: string | null;
}

export interface CountLine {
  stock_item_id: string;
  sku: string;
  name: string;
  stock_unit: string;
  counted: string;
  /** Snapshotted when the line was entered, not read at apply time. */
  expected: string;
  difference: string;
  variance_value: string;
}

export interface StockCount {
  id: string;
  storage_id: string;
  business_date: string;
  state: 'open' | 'applied' | 'abandoned';
  is_full: boolean;
  note: string | null;
  started_at: string;
  applied_at: string | null;
  variance_value: string | null;
  lines: CountLine[];
}

export interface AddStockItemInput {
  sku: string;
  name: string;
  stock_unit: string;
  category?: string;
  density?: string;
  pack_size?: string;
  pack_unit?: string;
  reorder_level?: string;
}

export interface ReceiveInput {
  stockItemId: string;
  storageId: string;
  quantity: string;
  unitCost: string;
  /** The unit the delivery arrived in. The price is per this unit too. */
  unitCode?: string;
  note?: string;
}

export interface MoveOutInput {
  stockItemId: string;
  storageId: string;
  quantity: string;
  unitCode?: string;
  note?: string;
}

export interface WasteInput extends MoveOutInput {
  reason: string;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['inventory'] });
}

export function useStockItems(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'items', includeInactive],
    queryFn: ({ signal }) =>
      api.get<StockItem[]>(`/v1/inventory/items?include_inactive=${includeInactive}`, signal),
    staleTime: 60_000,
  });
}

/**
 * The fourteen named in law, and the only values this API accepts.
 *
 * A closed list because free text gives one store "peanut", "peanuts" and
 * "Peanut (trace)", and a menu filtered on any one of them misses the other
 * two — somebody's allergy turned into somebody else's spelling.
 */
export const ALLERGENS = [
  'celery',
  'cereals_containing_gluten',
  'crustaceans',
  'eggs',
  'fish',
  'lupin',
  'milk',
  'molluscs',
  'mustard',
  'nuts',
  'peanuts',
  'sesame',
  'soybeans',
  'sulphur_dioxide',
] as const;

export type Allergen = (typeof ALLERGENS)[number];

export const ALLERGEN_LABELS: Record<Allergen, string> = {
  celery: 'Celery',
  cereals_containing_gluten: 'Cereals with gluten',
  crustaceans: 'Crustaceans',
  eggs: 'Eggs',
  fish: 'Fish',
  lupin: 'Lupin',
  milk: 'Milk',
  molluscs: 'Molluscs',
  mustard: 'Mustard',
  nuts: 'Tree nuts',
  peanuts: 'Peanuts',
  sesame: 'Sesame',
  soybeans: 'Soybeans',
  sulphur_dioxide: 'Sulphites',
};

/**
 * Record which of the fourteen an ingredient contains.
 *
 * An empty list is a declaration and not a blank: it says somebody looked and
 * found none. That is why this is its own call rather than a field on the item
 * edit — saving a form with an untouched box must not turn "nobody has said"
 * into "contains nothing", and only one of those is safe to print on a menu.
 */
export function useDeclareAllergens() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockItem, ApiError, { stockItemId: string; allergens: Allergen[] }>({
    mutationFn: ({ stockItemId, allergens }) =>
      api.request<StockItem>(`/v1/inventory/items/${stockItemId}/allergens`, {
        method: 'PUT',
        body: { allergens },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
      // A dish's allergens are rolled up from these, so the matrix behind it
      // is wrong the moment this lands.
      void queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });
}

export function useAddStockItem() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockItem, ApiError, AddStockItemInput>({
    mutationFn: (input) => api.post<StockItem>('/v1/inventory/items', input),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * What an item edit may change. Only the keys present are sent, and the server
 * treats them that way: `reorderLevel: null` clears the par ("nobody has said
 * what low is"), while leaving it out leaves the par alone. Units are not
 * here — a level is held in them, so changing one is a migration, not an edit.
 */
export interface UpdateStockItemInput {
  stockItemId: string;
  name?: string;
  category?: string | null;
  reorderLevel?: string | null;
}

/**
 * Change an item after it exists — above all its par.
 *
 * A reorder level used to be set once, on the day the item was added, and the
 * reorder screen and the low-stock alert have worked to it ever since. Trade
 * grows; the par has to be able to follow.
 */
export function useUpdateStockItem() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockItem, ApiError, UpdateStockItemInput>({
    mutationFn: ({ stockItemId, name, category, reorderLevel }) => {
      const body: Record<string, string | null> = {};
      if (name !== undefined) body.name = name;
      if (category !== undefined) body.category = category;
      if (reorderLevel !== undefined) body.reorder_level = reorderLevel;
      return api.request<StockItem>(`/v1/inventory/items/${stockItemId}`, {
        method: 'PATCH',
        body,
      });
    },
    // Levels carry the par and `below_reorder`, so they are stale too.
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Where an item is kept at a site, when that is not the site's default.
 *
 * Sales come out of here. An item with no entry is kept in the default storage
 * and its sales come out of that — which used to be true of every item, so a
 * site with a walk-in, a dry store and a bar sold out of one and filled the
 * others for ever.
 */
export interface StockHome {
  stock_item_id: string;
  location_id: string;
  /** Null when the item is kept in the site's default storage. */
  storage_id: string | null;
}

export function useStockHomes(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'homes', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<StockHome[]>(`/v1/inventory/homes?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * Say which storage an item is kept in at a site. `storageId: null` puts it
 * back on the default. Stock already on a shelf stays where it is — moving it
 * is a transfer — and only sales from the next closed tab on follow the change.
 */
export function useSetStockHome() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    StockHome,
    ApiError,
    { stockItemId: string; locationId: string; storageId: string | null }
  >({
    mutationFn: ({ stockItemId, locationId, storageId }) =>
      api.request<StockHome>(`/v1/inventory/items/${stockItemId}/home`, {
        method: 'PUT',
        body: { location_id: locationId, storage_id: storageId },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'homes'] });
    },
  });
}

export function useStorages(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'storages', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<Storage[]>(`/v1/inventory/storages?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 5 * 60_000,
  });
}

export function useAddStorage() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Storage,
    ApiError,
    { locationId: string; code: string; name: string; isDefault?: boolean }
  >({
    mutationFn: ({ locationId, code, name, isDefault = false }) =>
      api.post<Storage>('/v1/inventory/storages', {
        location_id: locationId,
        code,
        name,
        is_default: isDefault,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Rename a storage, change its code, or make it the site's default (which
 * takes it off the old one). 409 for a code another storage at the site uses.
 */
export function useUpdateStorage() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Storage,
    ApiError,
    { id: string; name?: string; code?: string; isDefault?: true }
  >({
    mutationFn: ({ id, name, code, isDefault }) =>
      api.request<Storage>(`/v1/inventory/storages/${id}`, {
        method: 'PATCH',
        body: {
          ...(name !== undefined ? { name } : {}),
          ...(code !== undefined ? { code } : {}),
          ...(isDefault ? { is_default: true } : {}),
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Take a storage away: deleted if nothing ever moved through it, retired with
 * its history if something did. 409, saying why, while it holds stock or is
 * the default of a site with others.
 */
export function useRemoveStorage() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.request<void>(`/v1/inventory/storages/${id}`, { method: 'DELETE' }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useStockLevels(storageId: string | undefined, lowOnly = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'levels', storageId ?? null, lowOnly],
    queryFn: ({ signal }) =>
      api.get<StockLevel[]>(
        `/v1/inventory/levels?storage_id=${storageId}&low_only=${lowOnly}`,
        signal,
      ),
    enabled: Boolean(storageId),
    staleTime: 30_000,
  });
}

export function useLowStock(storageId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'low', storageId ?? null],
    queryFn: ({ signal }) =>
      api.get<StockLevel[]>(`/v1/inventory/low?storage_id=${storageId}`, signal),
    enabled: Boolean(storageId),
    staleTime: 60_000,
  });
}

/** The history of one item — what answers "where did it go". */
export function useStockMovements(stockItemId: string | undefined, limit = 100) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'movements', stockItemId ?? null, limit],
    queryFn: ({ signal }) =>
      api.get<StockMovement[]>(
        `/v1/inventory/items/${stockItemId}/movements?limit=${limit}`,
        signal,
      ),
    enabled: Boolean(stockItemId),
    staleTime: 30_000,
  });
}

export function useReceiveStock() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockLevel, ApiError, ReceiveInput>({
    mutationFn: (input) =>
      api.post<StockLevel>('/v1/inventory/receive', {
        stock_item_id: input.stockItemId,
        storage_id: input.storageId,
        quantity: input.quantity,
        unit_cost: input.unitCost,
        unit_code: input.unitCode ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useConsumeStock() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockLevel, ApiError, MoveOutInput>({
    mutationFn: (input) =>
      api.post<StockLevel>('/v1/inventory/consume', {
        stock_item_id: input.stockItemId,
        storage_id: input.storageId,
        quantity: input.quantity,
        unit_code: input.unitCode ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useRecordWaste() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockLevel, ApiError, WasteInput>({
    mutationFn: (input) =>
      api.post<StockLevel>('/v1/inventory/waste', {
        stock_item_id: input.stockItemId,
        storage_id: input.storageId,
        quantity: input.quantity,
        reason: input.reason,
        unit_code: input.unitCode ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Find counts, open or finished.
 *
 * A chef counts in the walk-in and a manager applies it in the office, so the
 * sheet has to be reachable from a device that did not start it. Never cached:
 * the whole point is picking up what somebody else left open.
 */
export function useStockCounts(
  storageId: string | undefined,
  state?: 'open' | 'applied' | 'abandoned',
) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'counts', storageId ?? null, state ?? null],
    queryFn: ({ signal }) =>
      api.get<StockCount[]>(
        `/v1/inventory/counts?storage_id=${storageId}${state ? `&state=${state}` : ''}`,
        signal,
      ),
    enabled: Boolean(storageId),
    staleTime: 0,
  });
}

export function useStockCount(stockCountId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['inventory', 'count', stockCountId ?? null],
    queryFn: ({ signal }) =>
      api.get<StockCount>(`/v1/inventory/counts/${stockCountId}`, signal),
    enabled: Boolean(stockCountId),
    staleTime: 0,
  });
}

export function useStartCount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    StockCount,
    ApiError,
    { storageId: string; isFull?: boolean; note?: string }
  >({
    mutationFn: ({ storageId, isFull = false, note }) =>
      api.post<StockCount>('/v1/inventory/counts', {
        storage_id: storageId,
        is_full: isFull,
        note: note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Put a figure on the sheet. Nothing moves until the count is applied.
 *
 * Re-entering an item is ordinary — somebody found another case in the back —
 * and the server refreshes the expected figure with it so the pair still
 * describes the same moment.
 */
export function useCountItem() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    StockCount,
    ApiError,
    { countId: string; stockItemId: string; counted: string; unitCode?: string }
  >({
    mutationFn: ({ countId, stockItemId, counted, unitCode }) =>
      api.post<StockCount>(`/v1/inventory/counts/${countId}/lines`, {
        stock_item_id: stockItemId,
        counted,
        unit_code: unitCode ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Move the books to match the sheet. This is where the money moves. */
export function useApplyCount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockCount, ApiError, { countId: string }>({
    mutationFn: ({ countId }) => api.post<StockCount>(`/v1/inventory/counts/${countId}/apply`),
    onSettled: () => invalidate(queryClient),
  });
}

export function useAbandonCount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockCount, ApiError, { countId: string }>({
    mutationFn: ({ countId }) =>
      api.post<StockCount>(`/v1/inventory/counts/${countId}/abandon`),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * A quantity as a stock sheet would write it: "2 kg", "0.5 l", "-3 kg".
 *
 * The server stores six decimal places so 0.5 g of saffron and 25 kg of flour
 * can share a column. Printing that back says "2.000000 kg", which is the
 * database talking rather than the kitchen.
 */
export function formatQuantity(quantity: string, unit: string): string {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return `${quantity} ${unit}`;
  return `${value} ${unit}`;
}

/** The kinds, in the words a chef would use. */
export const MOVEMENT_LABELS: Record<MovementKind, string> = {
  receipt: 'Delivery',
  issue: 'Used',
  waste: 'Wasted',
  count: 'Count adjustment',
  transfer_in: 'Transferred in',
  transfer_out: 'Transferred out',
  return_out: 'Returned to supplier',
};

/**
 * Both ends of a move, and what it was worth.
 *
 * `value` is on the response because it is the thing a transfer must not
 * change: the stock left one shelf worth this and arrived on the other worth
 * the same. Showing it is how somebody confirms a move did what they meant,
 * without adding up two levels themselves.
 */
export interface StockTransfer {
  stock_item_id: string;
  quantity: string;
  /** Named as `origin` and `destination` name it — it was `unit` until 2026-09-28. */
  stock_unit: string;
  value: string;
  unit_cost: string;
  origin: StockLevel;
  destination: StockLevel;
}

export interface TransferInput {
  stockItemId: string;
  fromStorageId: string;
  toStorageId: string;
  quantity: string;
  /** Enter it in what the person is holding — a 2 kg block, a case. */
  unitCode?: string;
  note?: string;
}

/**
 * Move stock from one storage to another.
 *
 * **Not consume-and-receive.** That was the only way to do it before this
 * existed, and a receipt needs a unit cost — so somebody retyped one and
 * weighted-average costing treated the arrival as a fresh purchase. Moving
 * flour from the dry store to the bar quietly repriced the flour. A transfer
 * carries the origin's cost, so no price is asked for and none should be
 * offered on the form.
 *
 * Between storages at one location this needs no feature. Between locations it
 * is `inventory.transfers` and returns 402 without it, so a form that offers
 * storages from more than one site should expect that and show the upgrade
 * rather than a failure.
 */
export function useTransferStock() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, ApiError, TransferInput>({
    mutationFn: (input) =>
      api.post<StockTransfer>('/v1/inventory/transfer', {
        stock_item_id: input.stockItemId,
        from_storage_id: input.fromStorageId,
        to_storage_id: input.toStorageId,
        quantity: input.quantity,
        unit_code: input.unitCode ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}
